/**
 * Theme-aware table styling helpers
 * Provides consistent table styles that respond to light/dark themes
 */

/**
 * Get table header styles based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for table headers
 */
export const getTableHeaderStyle = (isDarkMode) => ({
  color: isDarkMode ? '#dddddd' : '#212529',
  backgroundColor: isDarkMode ? '#232531' : '#eeeeee',
  borderColor: isDarkMode ? '#434651' : '#eeeeee',
  whiteSpace: 'nowrap',
});

/**
 * Get table cell styles based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for table cells
 */
export const getTableCellStyle = (isDarkMode) => ({
  color: isDarkMode ? '#dddddd' : '#212529',
  backgroundColor: isDarkMode ? '#2a2d3a' : '#ffffff',
  borderColor: isDarkMode ? '#434651' : '#dee2e6',
});

/**
 * Get table wrapper/container background style based on theme
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} Style object for table wrapper
 */
export const getTableWrapperStyle = (isDarkMode) => ({
  backgroundColor: isDarkMode ? '#2a2d3a' : '#ffffff',
});

/**
 * Get alternating row styles for striped tables
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @param {boolean} isOddRow - Whether this is an odd row (for striping)
 * @returns {Object} Style object for alternating table rows
 */
export const getTableRowStyle = (isDarkMode, isOddRow = false) => {
  let backgroundColor;
  if (isOddRow) {
    backgroundColor = isDarkMode ? '#3a3e4d' : '#f8f9fa';
  } else {
    backgroundColor = isDarkMode ? '#2a2d3a' : '#ffffff';
  }

  return {
    color: isDarkMode ? '#dddddd' : '#212529',
    backgroundColor,
    borderColor: isDarkMode ? '#434651' : '#dee2e6',
    whiteSpace: 'nowrap',
  };
};

/**
 * Check if dark mode is active from theme object
 * @param {Object} theme - Theme object from Redux state
 * @returns {boolean} True if dark mode is active
 */
export const isDarkTheme = (theme) => theme.className && !theme.className.endsWith('light');
